import type { MeshStandardMaterial } from 'three/src/materials/MeshStandardMaterial';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { PerspectiveCamera } from 'three/src/cameras/PerspectiveCamera';
import { LinearToneMapping, sRGBEncoding } from 'three/src/constants';
import { DirectionalLight } from 'three/src/lights/DirectionalLight';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { WebGLRenderer } from 'three/src/renderers/WebGLRenderer';
import { AmbientLight } from 'three/src/lights/AmbientLight';
import type { ColorRepresentation } from 'three/src/utils';

import type { Object3D } from 'three/src/core/Object3D';
import type { Group } from 'three/src/objects/Group';
import type { Mesh } from 'three/src/objects/Mesh';
import { Vector3 } from 'three/src/math/Vector3';
import { Scene } from 'three/src/scenes/Scene';
import AvatarHeadshot from './AvatarHeadshot';
import { Color } from 'three/src/math/Color';
import RAF from '@/utils/RAF';
import anime from 'animejs';

import type {
  IAvatarOptions,
  IAvatarElement
} from '@/components/AvatarOptionsPanel/types';

export default class AvatarPreview {
  private avatar!: Group;
  private zoomed = false;
  private lastTouch = 0.0;

  private mouth?: Object3D;
  private controls = false;
  private autoRotate = true;
  public onLoad?: () => void;
  private expression?: string;

  private renderer!: WebGLRenderer;
  private readonly baseScale = 0.05;
  private camera!: PerspectiveCamera;
  private readonly update: () => void;

  private readonly scene = new Scene();
  private orbitControls!: OrbitControls;
  private readonly close = new Vector3();
  private directional!: DirectionalLight;
  private currentOptions?: IAvatarOptions;

  private readonly headshot: AvatarHeadshot;
  private readonly loader = new GLTFLoader();
  private readonly direction = new Vector3();

  private readonly customMaterialName = 'Custom';
  private readonly onTouchEnd = this.touchEnd.bind(this);
  private readonly onTouchMove = this.touchMove.bind(this);
  private readonly onTouchStart = this.touchStart.bind(this);
  private readonly baseChildren = ['BODY', 'mixamorig_Hips_2'];

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly baseModelUrl: string,
    screenshoot: HTMLCanvasElement,
    options: IAvatarOptions
  ) {
    const { offsetWidth, offsetHeight } = this.canvas;
    this.headshot = new AvatarHeadshot(screenshoot);

    this.createRenderer(offsetWidth, offsetHeight);
    this.createCamera(offsetWidth / offsetHeight);

    this.loader.setMeshoptDecoder(MeshoptDecoder);

    this.update = this.render.bind(this);
    this.loadBaseModel(options);

    this.createControls();
    this.createLights();

    RAF.add(this.update);
    RAF.pause = false;
  }

  private createRenderer(width: number, height: number): void {
    this.renderer = new WebGLRenderer({
      powerPreference: 'high-performance',
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });

    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 3.0));
    this.renderer.debug.checkShaderErrors = import.meta.env.DEV;
    this.renderer.setClearColor(Color.NAMES.black, 0.0);

    this.renderer.toneMapping = LinearToneMapping;
    this.renderer.outputEncoding = sRGBEncoding;
    this.renderer.setSize(width, height);
  }

  private createCamera(ratio: number): void {
    this.camera = new PerspectiveCamera(50.0, ratio, 0.1, 100.0);
    this.camera.position.set(0.0, 27.5, 47.5);
    this.scene.add(this.camera);
  }

  private async loadBaseModel(options: IAvatarOptions): Promise<void> {
    const avatar = await this.loader.loadAsync(
      this.baseModelUrl,
      this.onProgress.bind(this)
    );

    const { children } = avatar.scene.children[0];

    this.expression = options.elements.find(({ name }) =>
      name.includes('Expression')
    )?.name;

    for (let child = children.length; child--; ) {
      if (!this.baseChildren.includes(children[child].name))
        children[child].visible = false;

      if (this.expression === children[child].name) {
        const mouth = children[child].getObjectByName(this.expression);
        this.setMouthColor(mouth?.children[0] as Mesh, 0x810204, 0.55);
        this.mouth = mouth?.children[1] as Object3D;
      }
    }

    avatar.scene.scale.setScalar(this.baseScale);
    this.scene.add(avatar.scene);
    this.avatar = avatar.scene;
    this.options = options;
    this.onLoad?.();
  }

  private onProgress(event: ProgressEvent): void {
    const progress = ~~((10.0 * (event.loaded * 100.0)) / event.total);
    console.info(`Loading... ${progress / 10.0}%`);
  }

  private touchStart(event: TouchEvent): void {
    this.autoRotate = false;
    if (this.controls) return;
    this.lastTouch = event.touches[0].clientX;
  }

  private touchMove(event: TouchEvent): void {
    if (this.controls) return;

    const { clientX } = event.touches[0];
    const x = this.lastTouch - clientX;

    this.avatar.rotation.y -= x * 0.02;
    this.lastTouch = clientX;
  }

  private touchEnd(): void {
    this.autoRotate = true;
  }

  private createControls(): void {
    this.renderer.domElement.addEventListener('touchstart', this.onTouchStart);
    this.renderer.domElement.addEventListener('touchmove', this.onTouchMove);
    this.renderer.domElement.addEventListener('touchend', this.onTouchEnd);

    this.orbitControls = new OrbitControls(
      this.camera,
      this.renderer.domElement
    );

    this.orbitControls.minPolarAngle = Math.PI * 0.4;
    this.orbitControls.maxPolarAngle = Math.PI * 0.6;
    this.orbitControls.target.set(0.0, 21.0, 0.0);

    this.orbitControls.enableDamping = true;
    this.orbitControls.minDistance = 30.0;
    this.orbitControls.maxDistance = 60.0;
    this.orbitControls.rotateSpeed = 0.8;

    this.orbitControls.enablePan = false;
    this.orbitControls.enabled = false;
    this.orbitControls.update();
  }

  private createLights(): void {
    this.directional = new DirectionalLight(Color.NAMES.white, 1.0);
    const ambient = new AmbientLight(Color.NAMES.white, 0.25);

    this.directional.lookAt(this.orbitControls.target);
    this.directional.position.set(0.0, 50.0, 47.0);

    this.scene.add(this.directional);
    this.scene.add(ambient);
  }

  public set options(options: IAvatarOptions) {
    this.currentOptions?.age !== options.age && this.setScaleByAge(options.age);

    this.currentOptions?.skinColor !== options.skinColor &&
      this.setSkinColor(options.skinColor);

    this.currentOptions?.elements.forEach(this.hideElement.bind(this));

    options.elements.length &&
      options.elements.forEach(this.showElement.bind(this));

    this.currentOptions = { ...options };
  }

  private setScaleByAge(size: number): void {
    const scale = size === 1 ? 70.0 : size === 2 ? 85.0 : 100.0;
    const y = size === 1 ? 23.8 : size === 2 ? 28.9 : 34.0;
    const z = size === 1 ? 13.0 : size === 2 ? 16.5 : 20.0;

    this.avatar.scale.setScalar(this.baseScale * scale);
    this.close.set(0.0, y, z);
  }

  private setSkinColor(color: number): void {
    const body = this.avatar.getObjectByName(this.baseChildren[0]);
    this.updateElementMaterial(body?.children[0] as Object3D, color);
    this.updateElementMaterial(this.mouth as Object3D, color);
  }

  private hideElement({ name }: IAvatarElement): void {
    const element = this.avatar.getObjectByName(name);
    if (element) element.visible = false;
  }

  private showElement({ name, color }: IAvatarElement): void {
    const element = this.avatar.getObjectByName(name);
    if (!element) return console.warn(`Element "${name}" not found!`);

    this.expression !== name &&
      element.traverse((child) => this.updateElementMaterial(child, color));

    element.visible = true;
  }

  private setMouthColor(mouth: Mesh, color: number, alpha: number): void {
    const material = mouth.material as MeshStandardMaterial;
    material.emissiveIntensity = alpha;
    material.emissive.set(color);
  }

  public resize(controls: boolean): void {
    const { offsetWidth, offsetHeight } = this.canvas;

    this.renderer.setSize(offsetWidth, offsetHeight);
    this.camera.aspect = offsetWidth / offsetHeight;
    this.camera.updateProjectionMatrix();

    this.orbitControls.target.set(0.0, 21.0, 0.0);
    this.camera.position.set(0.0, 27.5, 47.5);

    this.orbitControls.enabled = controls;
    this.orbitControls.update();

    if ((this.controls = controls)) {
      this.directional.position.set(0.0, 22.5, 2.5);
      this.scene.remove(this.directional);
      this.camera.add(this.directional);
      this.avatar.rotation.y = 0.0;
    } else {
      this.directional.position.set(0.0, 50.0, 47.0);
      this.camera.remove(this.directional);
      this.scene.add(this.directional);
    }
  }

  public captureScreenshot(): string {
    // this.directional.position.set(0.0, 22.5, 2.5);
    // this.scene.remove(this.directional);
    // this.camera.add(this.directional);

    const y = this.avatar.rotation.y;
    this.avatar.rotation.y = 0.0;

    const { age = 3.0 } = this.currentOptions ?? {};
    const head = this.headshot.capture(this.avatar, age);

    // this.directional.position.set(0.0, 50.0, 47.0);
    // this.camera.remove(this.directional);
    // this.scene.add(this.directional);

    this.scene.add(this.avatar);
    this.avatar.rotation.y = y;
    return head;
  }

  public set zoom(zoom: boolean) {
    if (this.zoomed === zoom) return;
    this.autoRotate = false;

    if ((this.zoomed = zoom)) {
      this.direction
        .subVectors(this.camera.position, this.close)
        .setLength(1.0)
        .add(this.close);
    } else {
      this.direction.set(0.0, 27.5, 47.5);
    }

    const { x, y, z } = this.direction;

    anime({
      complete: () => (this.autoRotate = true),
      targets: this.camera.position,
      easing: 'easeInOutQuad',
      duration: 500,
      x,
      y,
      z
    });
  }

  private updateElementMaterial(
    element: Object3D,
    color?: ColorRepresentation
  ): void {
    const mesh = element as Mesh;
    if (!mesh.material) return;

    let material = mesh.material as MeshStandardMaterial;
    if (!material.name.includes(this.customMaterialName)) return;

    color ??= material.color.clone();
    material = material.clone();

    material.color.set(color).convertSRGBToLinear();
    mesh.material = material;
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
    this.controls && this.orbitControls.update();

    if (this.avatar && this.autoRotate) {
      this.avatar.rotation.y -= 0.01;
    }
  }

  public dispose(): void {
    this.renderer.domElement.removeEventListener('touchmove', this.onTouchMove);

    this.renderer.domElement.removeEventListener(
      'touchstart',
      this.onTouchStart
    );

    this.renderer.domElement.remove();
    this.orbitControls.dispose();
    this.renderer.dispose();

    this.headshot.dispose();
    RAF.remove(this.update);
    this.scene.clear();
  }
}
